import React, { useState, useEffect, useRef, useCallback } from "react";
import cancel from "../assets/images/navbar/cancel.png";
import search from "../assets/images/navbar/search.png";

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: LocationData) => void;
  initialLocation?: {
    lat: number;
    lng: number;
  };
  googleMapsApiKey: string;
}

export interface LocationData {
  address: string;
  pincode: string;
  city: string;
  state: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  fullAddress: string;
}

declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  initialLocation,
  googleMapsApiKey,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dragTimeoutRef = useRef<any>(null);

  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedLocation(null);
      setIsDragging(false);
      setIsLoadingMap(true);
    }
  }, [isOpen]);

  const reverseGeocode = useCallback((lat: number, lng: number) => {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode(
      { location: { lat, lng } },
      (results: any[], status: string) => {
        if (status === "OK" && results[0]) {
          const result = results[0];
          const addressComponents = result.address_components;

          let address = "";
          let pincode = "";
          let city = "";
          let state = "";

          // Extract address components
          addressComponents.forEach((component: any) => {
            const types = component.types;
            if (types.includes("postal_code")) {
              pincode = component.long_name;
            }
            if (
              types.includes("locality") ||
              types.includes("administrative_area_level_2")
            ) {
              city = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              state = component.long_name;
            }
            if (
              types.includes("sublocality_level_1") ||
              types.includes("sublocality")
            ) {
              address = component.long_name;
            }
          });

          // If no specific address found, use route or formatted address
          if (!address) {
            const route = addressComponents.find((c: any) =>
              c.types.includes("route"),
            );
            address = route
              ? route.long_name
              : result.formatted_address.split(",")[0];
          }

          const locationData: LocationData = {
            address,
            pincode,
            city,
            state,
            coordinates: { lat, lng },
            fullAddress: result.formatted_address,
          };

          setSelectedLocation(locationData);
        }
      },
    );
  }, []);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    // Default to Kolkata or initial location
    const defaultLocation = initialLocation || { lat: 22.5726, lng: 88.3639 };

    // Create map
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultLocation,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: 'greedy', // Allow one-finger panning on mobile
    });

    // Create geocoder
    geocoderRef.current = new window.google.maps.Geocoder();

    // Add map drag start listener
    mapInstanceRef.current.addListener("dragstart", () => {
      setIsDragging(true);
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    });

    // Add map drag end listener - get center location when user stops dragging
    mapInstanceRef.current.addListener("dragend", () => {
      setIsDragging(false);
      dragTimeoutRef.current = setTimeout(() => {
        const center = mapInstanceRef.current.getCenter();
        reverseGeocode(center.lat(), center.lng());
      }, 300);
    });

    // Add map center changed listener for zoom
    mapInstanceRef.current.addListener("zoom_changed", () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      dragTimeoutRef.current = setTimeout(() => {
        const center = mapInstanceRef.current.getCenter();
        reverseGeocode(center.lat(), center.lng());
      }, 500);
    });

    // Autocomplete is now initialized in a separate useEffect to avoid race conditions

    setIsLoadingMap(false);

    // Get initial location details
    reverseGeocode(defaultLocation.lat, defaultLocation.lng);
  }, [initialLocation, reverseGeocode]);

  // Load Google Maps Script
  useEffect(() => {
    if (!isOpen) return;

    const loadGoogleMaps = () => {
      // Check if already loaded
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      // Load script
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeMap();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps");
        setIsLoadingMap(false);
        alert("Failed to load Google Maps. Please check your API key.");
      };
      document.head.appendChild(script);
    };

    loadGoogleMaps();

    // Cleanup function
    return () => {
      // Clear autocomplete listener when modal closes
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isOpen, googleMapsApiKey, initializeMap]);

  // Separate effect for autocomplete initialization - ensures input ref is ready
  useEffect(() => {
    if (!isOpen || !window.google || !window.google.maps || !window.google.maps.places) return;

    // Clear any existing autocomplete before creating new one
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (searchInputRef.current) {
        try {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            {
              componentRestrictions: { country: "in" },
              fields: ["address_components", "geometry", "formatted_address"],
            },
          );

          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry && place.geometry.location) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setCenter({ lat, lng });
                reverseGeocode(lat, lng);
              }
            }
          });

          console.log("Autocomplete initialized successfully");
        } catch (error) {
          console.error("Error initializing autocomplete:", error);
        }
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen, reverseGeocode]);

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat, lng });
          reverseGeocode(lat, lng);
        }

        setIsLoadingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Unable to get your location. Please try again.");
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation);
      onClose();
    } else {
      alert("Please select a location on the map");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1002] flex items-center justify-center bg-black/50 p-0 md:p-4">
      <div className="relative w-full h-full md:max-w-[800px] md:max-h-[90vh] md:h-auto bg-white dark:bg-gray-800 md:rounded-[20px] shadow-xl flex flex-col overflow-hidden pb-16 md:pb-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Select Delivery Location
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <img src={cancel} alt="close" className="h-5 w-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-5 py-3 border-b dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
            <img src={search} alt="search" className="h-4 w-4 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for area, street name..."
              className="flex-1 border-none p-0 text-sm outline-none bg-transparent placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative min-h-[300px] overflow-hidden">
          {isLoadingMap && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">
                  Loading map...
                </p>
              </div>
            </div>
          )}
          <div ref={mapRef} className="w-full h-full min-h-[300px]" />

          {/* Fixed Center Marker Pointer */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full pointer-events-none z-10">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform ${isDragging ? "scale-110" : "scale-100"}`}
            >
              <g filter="url(#shadow)">
                <path
                  d="M24 44C24 44 38 30 38 18C38 10.268 31.732 4 24 4C16.268 4 10 10.268 10 18C10 30 24 44 24 44Z"
                  fill="#7402A7"
                />
                <path
                  d="M24 24C27.3137 24 30 21.3137 30 18C30 14.6863 27.3137 12 24 12C20.6863 12 18 14.6863 18 18C18 21.3137 20.6863 24 24 24Z"
                  fill="white"
                />
              </g>
              <defs>
                <filter
                  id="shadow"
                  x="6"
                  y="2"
                  width="36"
                  height="46"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="2" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>

          {/* Current Location Button (Floating on Map) */}
          <button
            onClick={handleCurrentLocation}
            disabled={isLoadingLocation}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-shadow disabled:opacity-50 z-10"
          >
            {isLoadingLocation ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            ) : (
              <div className="flex gap-2 items-center">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="3" fill="#7402A7" />
                  <path
                    d="M12 2v4m0 12v4m10-10h-4m-12 0H2"
                    stroke="#7402A7"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="8"
                    stroke="#7402A7"
                    strokeWidth="2"
                  />
                </svg>
                <span className="text-gray-900 dark:text-white text-sm font-medium">Current Location</span>
              </div>
            )}
          </button>

          {/* Instruction Text Overlay
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg z-10">
            <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
              {isDragging ? 'Release to select location' : 'Place the pin at exact delivery location'}
            </p>
          </div> */}
        </div>

        {/* Location Details Card & Button Section - Fixed at bottom */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700">
          {selectedLocation && (
            <div className="px-5 py-4 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 13.43C13.7231 13.43 15.12 12.0331 15.12 10.31C15.12 8.58687 13.7231 7.19 12 7.19C10.2769 7.19 8.88 8.58687 8.88 10.31C8.88 12.0331 10.2769 13.43 12 13.43Z"
                      stroke="#7402A7"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M3.62001 8.49C5.59001 -0.169998 18.42 -0.159997 20.38 8.5C21.53 13.58 18.37 17.88 15.6 20.54C13.59 22.48 10.41 22.48 8.39001 20.54C5.63001 17.88 2.47001 13.57 3.62001 8.49Z"
                      stroke="#7402A7"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {selectedLocation.pincode && `${selectedLocation.pincode}`}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 break-words">
                    {selectedLocation.fullAddress}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Confirm Button */}
          <div className="px-5 py-4">
            <button
              onClick={handleConfirm}
              disabled={!selectedLocation}
              className="w-full px-8 py-3 rounded-full bg-[#7402A7] text-white font-medium hover:bg-[#5f0384] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm & proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
